import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, FileText, Clock, AlertTriangle, Users, Scale, FileCheck, Mail, Clipboard, CheckCircle, Loader2, Mic, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import MatrixBackground from '@/MatrixBackground';
import AnimatedToolTitle from '@/components/AnimatedToolTitle';
import AnimatedActionButton from '@/components/AnimatedActionButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InstructionAction = ({ onBack }) => {
    // Set page title
  useEffect(() => {
    document.title = "Instruction Action - Lawra AI";
    return () => {
      document.title = "Lawra AI - AI Legal Assistant for Lawyers & Law Firms | Save 70-80% Time";
    };
  }, []);

  const navigate = useNavigate();
  const [inputMode, setInputMode] = useState('text'); // 'text' or 'audio'
  const [instructions, setInstructions] = useState('');
  const [audioFile, setAudioFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  
  // Extracted structured data
  const [structuredData, setStructuredData] = useState(null);
  const [triageData, setTriageData] = useState(null);
  const [recommendedActions, setRecommendedActions] = useState([]);
  
  // Generation states
  const [generatingAction, setGeneratingAction] = useState(null);
  const [additionalInfo, setAdditionalInfo] = useState({});

  const handleAudioUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('audio/') || file.type === 'video/mp4' || file.type === 'video/webm') {
        setAudioFile(file);
        toast.success(`Audio file "${file.name}" uploaded`);
      } else {
        toast.error('Please upload a valid audio or video file');
      }
    }
  };

  const processInstructions = async () => {
    if (!instructions.trim() && !audioFile) {
      toast.error('Please provide instructions via text or audio');
      return;
    }

    setLoading(true);
    setAnalyzing(true);

    try {
      let processedInstructions = instructions;

      // Step 1: Transcribe audio if provided
      if (audioFile) {
        toast.info('Transcribing audio...');
        const formData = new FormData();
        formData.append('audio', audioFile);

        const transcriptionResponse = await axios.post(`${API}/instruction-action/transcribe`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        processedInstructions = transcriptionResponse.data.transcription;
        setInstructions(processedInstructions);
        toast.success('Audio transcribed successfully');
      }

      // Step 2: Extract structured data
      toast.info('Analyzing instructions...');
      const analysisResponse = await axios.post(`${API}/instruction-action/analyze`, {
        instructions: processedInstructions
      });

      setStructuredData(analysisResponse.data.structured);
      setTriageData(analysisResponse.data.triage);
      setRecommendedActions(analysisResponse.data.recommendations);
      
      setAnalyzing(false);
      toast.success('Analysis complete! Review the recommendations below.');
    } catch (error) {
      console.error('Error processing instructions:', error);
      toast.error(error.response?.data?.detail || 'Error processing instructions');
      setAnalyzing(false);
    } finally {
      setLoading(false);
    }
  };

  const generateAction = async (action) => {
    setGeneratingAction(action.id);

    try {
      const response = await axios.post(`${API}/instruction-action/generate`, {
        actionType: action.type,
        structuredData,
        triageData,
        additionalInfo: additionalInfo[action.id] || '',
        originalInstructions: instructions
      });

      // Navigate to output page with the generated content
      navigate('/tools/instruction-action/output', {
        state: {
          actionTitle: action.title,
          actionType: action.type,
          output: response.data.output,
          structuredData,
          triageData
        }
      });

      toast.success(`${action.title} generated successfully!`);
    } catch (error) {
      console.error('Error generating action:', error);
      toast.error('Error generating output');
      setGeneratingAction(null);
    }
  };

  const getActionIcon = (type) => {
    const icons = {
      'matter_summary': FileText,
      'file_note': Clipboard,
      'engagement_scope': FileCheck,
      'task_list': CheckCircle,
      'email_response': Mail,
      'contract': Scale,
      'document': FileText
    };
    return icons[type] || FileText;
  };

  const resetForm = () => {
    setInstructions('');
    setAudioFile(null);
    setStructuredData(null);
    setTriageData(null);
    setRecommendedActions([]);
    setAdditionalInfo({});
    setInputMode('text');
  };

  return (
    <div className="tool-page">
      <MatrixBackground />
      <div className="tool-header">
        <Button
          variant="ghost"
          onClick={() => onBack ? onBack() : navigate('/dashboard')}
          className="back-button"
        >
          <ArrowLeft size={20} />
          Back to Dashboard
        </Button>
        <div className="tool-title-section">
          <Clipboard size={40} className="tool-icon gold-icon" />
          <div>
            <AnimatedToolTitle title="Instruction & Action" />
            <p className="tool-subtitle">Transform client instructions into structured actions and documents</p>
          </div>
        </div>
      </div>

      <div className="tool-content">
        {/* Step 1: Input Instructions */}
        {!structuredData && (
          <Card className="generation-form-card">
            <CardHeader>
              <CardTitle>Step 1: Capture Instructions</CardTitle>
              <CardDescription>
                Paste client notes/transcript or upload an audio recording from a call/meeting
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Input Mode Toggle */}
              <div className="flex gap-4 mb-6">
                <Button
                  onClick={() => setInputMode('text')}
                  variant={inputMode === 'text' ? 'default' : 'outline'}
                  className={inputMode === 'text' ? 'bg-[#D4AF37] text-black' : ''}
                >
                  <FileText className="mr-2" size={16} />
                  Text Input
                </Button>
                <Button
                  onClick={() => setInputMode('audio')}
                  variant={inputMode === 'audio' ? 'default' : 'outline'}
                  className={inputMode === 'audio' ? 'bg-[#D4AF37] text-black' : ''}
                >
                  <Mic className="mr-2" size={16} />
                  Audio Upload
                </Button>
              </div>

              {/* Notetaker Tool Link */}
              <div className="notetaker-link-container" style={{ marginTop: '0.75rem', padding: '0.5rem 0', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                Don't have an audio or transcript yet? Use the Notetaker tool to record your meeting/call and create a transcript leading to other actionable tasks: 
                <Button
                  variant="link"
                  onClick={() => navigate('/notetaker')}
                  style={{ 
                    color: '#D4AF37', 
                    padding: '0 0.25rem',
                    textDecoration: 'underline',
                    height: 'auto',
                    fontSize: '0.9rem'
                  }}
                >
                  Notetaker
                </Button>
              </div>

              {inputMode === 'text' ? (
                <div>
                  <Label>Paste Client Instructions</Label>
                  <Textarea
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    placeholder="Paste your notes here... e.g., 'Client John Smith called regarding employment dispute. Was terminated on 15th Jan. Wants to pursue unfair dismissal claim. Budget up to £10k. Needs response by Friday...'"
                    rows={12}
                    className="mt-2"
                  />
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <Label>Upload Audio Recording</Label>
                    <p style={{ fontSize: '0.9rem', color: '#888' }}>
                      Not yet recorded a transcript?{' '}
                      <a 
                        href="/notetaker" 
                        style={{ color: 'var(--gold)', textDecoration: 'underline', cursor: 'pointer' }}
                        onClick={(e) => { e.preventDefault(); navigate('/notetaker'); }}
                      >
                        Record one here
                      </a>
                    </p>
                  </div>
                  <div className="mt-2 border-2 border-dashed rounded-lg p-8 text-center upload-area">
                    <Input
                      type="file"
                      accept="audio/*,video/mp4,video/webm"
                      onChange={handleAudioUpload}
                      className="hidden"
                      id="audio-upload"
                    />
                    <label htmlFor="audio-upload" className="cursor-pointer">
                      <Upload className="mx-auto mb-4 text-[#D4AF37]" size={48} />
                      <p className="mb-2">
                        {audioFile ? audioFile.name : 'Click to upload audio file'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Supports MP3, WAV, M4A, MP4 (Zoom recordings)
                      </p>
                    </label>
                  </div>
                  
                  {audioFile && instructions && (
                    <div className="mt-4">
                      <Label>Transcription</Label>
                      <Textarea
                        value={instructions}
                        onChange={(e) => setInstructions(e.target.value)}
                        rows={8}
                        className="mt-2"
                      />
                    </div>
                  )}
                </div>
              )}

              <AnimatedActionButton
                onClick={processInstructions}
                disabled={loading || (!instructions.trim() && !audioFile)}
                loading={loading}
                size="lg"
                className="mt-6"
              >
                Analyze Instructions
              </AnimatedActionButton>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Structured Data */}
        {structuredData && (
          <>
            <Card className="result-card">
              <CardHeader>
                <CardTitle>Step 2: Extracted Information</CardTitle>
                <CardDescription>
                  AI-extracted key details from your instructions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Parties */}
                  <div className="info-box">
                    <div className="flex items-center gap-2 mb-3">
                      <Users className="text-[#D4AF37]" size={20} />
                      <h4 className="font-semibold">Parties</h4>
                    </div>
                    <p>{structuredData.parties || 'Not specified'}</p>
                  </div>

                  {/* Matter Type */}
                  <div className="info-box">
                    <div className="flex items-center gap-2 mb-3">
                      <Scale className="text-[#D4AF37]" size={20} />
                      <h4 className="font-semibold">Matter Type</h4>
                    </div>
                    <p>{structuredData.matterType || 'Not specified'}</p>
                  </div>

                  {/* Deadlines */}
                  <div className="info-box">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="text-[#D4AF37]" size={20} />
                      <h4 className="font-semibold">Deadlines</h4>
                    </div>
                    <p>{structuredData.deadlines || 'None specified'}</p>
                  </div>

                  {/* Budget */}
                  <div className="info-box">
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="text-[#D4AF37]" size={20} />
                      <h4 className="font-semibold">Budget</h4>
                    </div>
                    <p>{structuredData.budget || 'Not specified'}</p>
                  </div>
                </div>

                {/* Key Facts */}
                <div className="mt-6 info-box">
                  <h4 className="font-semibold mb-3">Key Facts</h4>
                  <p>{structuredData.keyFacts || 'None extracted'}</p>
                </div>

                {/* Risk Flags */}
                {structuredData.riskFlags && (
                  <div className="mt-6 risk-box">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="text-red-500" size={20} />
                      <h4 className="font-semibold text-red-500">Risk Flags</h4>
                    </div>
                    <p>{structuredData.riskFlags}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Step 3: Triage */}
            <Card className="result-card">
              <CardHeader>
                <CardTitle>Step 3: Triage & Routing</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="triage-box">
                    <Scale className="mx-auto mb-2 text-[#D4AF37]" size={32} />
                    <h4 className="font-semibold">Area of Law</h4>
                    <p className="mt-2 text-[#D4AF37] font-bold">{triageData.areaOfLaw}</p>
                  </div>
                  <div className="triage-box">
                    <Clock className="mx-auto mb-2 text-[#D4AF37]" size={32} />
                    <h4 className="font-semibold">Urgency</h4>
                    <p className="mt-2 text-[#D4AF37] font-bold">{triageData.urgency}</p>
                  </div>
                  <div className="triage-box">
                    <AlertTriangle className="mx-auto mb-2 text-[#D4AF37]" size={32} />
                    <h4 className="font-semibold">Conflicts/KYC</h4>
                    <p className="mt-2 text-[#D4AF37] font-bold">{triageData.conflicts || 'None detected'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Step 4: Recommended Actions */}
            <Card className="result-card">
              <CardHeader>
                <CardTitle>Step 4: Recommended Actions</CardTitle>
                <CardDescription>
                  Click on any action to generate the document or output
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {recommendedActions.map((action) => {
                    const Icon = getActionIcon(action.type);
                    const isGenerating = generatingAction === action.id;

                    return (
                      <Card 
                        key={action.id}
                        className="action-card"
                      >
                        <CardHeader>
                          <div className="flex items-center gap-3">
                            <Icon className="text-[#D4AF37]" size={24} />
                            <CardTitle className="text-base">
                              {action.title}
                            </CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm mb-4 text-muted-foreground">
                            {action.description}
                          </p>

                          {!isGenerating && (
                            <>
                              <Textarea
                                placeholder="Add any additional information (optional)"
                                value={additionalInfo[action.id] || ''}
                                onChange={(e) => setAdditionalInfo({
                                  ...additionalInfo,
                                  [action.id]: e.target.value
                                })}
                                rows={2}
                                className="mb-3 text-sm"
                              />
                              <Button
                                onClick={() => generateAction(action)}
                                className="w-full bg-[#D4AF37] text-black hover:bg-[#E5B200]"
                                size="sm"
                              >
                                Generate
                              </Button>
                            </>
                          )}

                          {isGenerating && (
                            <div className="flex items-center justify-center py-4">
                              <Loader2 className="animate-spin text-[#D4AF37]" size={24} />
                              <span className="ml-2 text-[#D4AF37]">Generating...</span>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-4">
              <Button
                onClick={resetForm}
                variant="outline"
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[rgba(212,175,55,0.1)]"
              >
                Start New Analysis
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InstructionAction;
